import { useTranslation } from 'react-i18next'
import { CustomerPipelineFilters } from '../types'
import { getStatusTransitions } from '../services'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { SCHEMES } from '@/components/EchartCustomized'

interface StatusTransitionsChartProps {
    filters: CustomerPipelineFilters
}

// Chỉ track 3 trạng thái chính: contacts (searching) → poc → close_win
const STATUS_COLORS: Record<string, string> = {
    contacts: SCHEMES.info[0],
    poc: SCHEMES.warning[0],
    close_win: SCHEMES.success[0],
}

const StatusTransitionsChart: React.FC<StatusTransitionsChartProps> = ({
    filters,
}) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'status-transitions',
                filters.gte,
                filters.lte,
            ],
            queryFn: () => getStatusTransitions(filters),
            enabled: !!filters.gte && !!filters.lte,
        },
        {
            loadingComponent: (
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <div className="flex justify-center items-center h-[350px]">
                        <Loading.CircularProgress width={32} height={32} />
                    </div>
                </div>
            ),
        }
    )

    const chartData = useMemo(() => {
        if (!data) return []

        // Group transitions by date, gom về 3 trạng thái chính
        // contacts = từ searching chuyển đi (from_status = searching)
        // poc = chuyển sang poc (to_status = poc)
        // close_win = chuyển sang close_win (to_status = close_win)
        const groupedByDate = data.reduce(
            (acc, item) => {
                const date = item.date
                if (!acc[date]) {
                    acc[date] = {
                        date,
                        contacts: 0,
                        poc: 0,
                        close_win: 0,
                    }
                }
                // Contacts: đếm số chuyển từ searching (bắt đầu pipeline)
                if (item.from_status === 'searching') {
                    acc[date].contacts += item.count
                }
                // POC: đếm số chuyển sang poc
                if (item.to_status === 'poc') {
                    acc[date].poc += item.count
                }
                // Close Win: đếm số chuyển sang close_win
                if (item.to_status === 'close_win') {
                    acc[date].close_win += item.count
                }
                return acc
            },
            {} as Record<
                string,
                {
                    date: string
                    contacts: number
                    poc: number
                    close_win: number
                }
            >
        )

        return Object.values(groupedByDate).sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        )
    }, [data])

    const totalTransitions = useMemo(() => {
        if (!data) return 0
        return data.reduce((sum, item) => sum + item.count, 0)
    }, [data])

    const transitionsByStatus = useMemo(() => {
        if (!data) return { contacts: 0, poc: 0, close_win: 0 }
        return data.reduce(
            (acc, item) => {
                // Contacts: từ searching chuyển đi
                if (item.from_status === 'searching') {
                    acc.contacts += item.count
                }
                // POC: chuyển sang poc
                if (item.to_status === 'poc') {
                    acc.poc += item.count
                }
                // Close Win: chuyển sang close_win
                if (item.to_status === 'close_win') {
                    acc.close_win += item.count
                }
                return acc
            },
            { contacts: 0, poc: 0, close_win: 0 }
        )
    }, [data])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <Typogaphy className="font-semibold text-primary capitalize">
                        {t('status_transitions')}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500">
                        {t('daily_status_changes')}
                    </Typogaphy>
                </div>
                <div className="text-right">
                    <Typogaphy className="font-medium text-primary">
                        {totalTransitions.toLocaleString()}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500">
                        {t('total_transitions')}
                    </Typogaphy>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="flex justify-center items-center h-[300px] text-gray-500">
                    {t('no_data_available')}
                </div>
            ) : (
                <>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient
                                    id="colorContacts"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={STATUS_COLORS.contacts}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={STATUS_COLORS.contacts}
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                                <linearGradient
                                    id="colorPoc"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={STATUS_COLORS.poc}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={STATUS_COLORS.poc}
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                                <linearGradient
                                    id="colorCloseWin"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={STATUS_COLORS.close_win}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={STATUS_COLORS.close_win}
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                            />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(value) =>
                                    dayjs(value).format('DD/MM')
                                }
                                stroke="#9ca3af"
                                fontSize={12}
                            />
                            <YAxis stroke="#9ca3af" fontSize={12} />
                            <Tooltip
                                labelFormatter={(value) =>
                                    dayjs(value).format('DD/MM/YYYY')
                                }
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                }}
                            />
                            <Legend />
                            <Area
                                type="monotone"
                                dataKey="contacts"
                                stroke={STATUS_COLORS.contacts}
                                strokeWidth={2}
                                fill="url(#colorContacts)"
                                name={t('total_contacts')}
                            />
                            <Area
                                type="monotone"
                                dataKey="poc"
                                stroke={STATUS_COLORS.poc}
                                strokeWidth={2}
                                fill="url(#colorPoc)"
                                name={t('poc')}
                            />
                            <Area
                                type="monotone"
                                dataKey="close_win"
                                stroke={STATUS_COLORS.close_win}
                                strokeWidth={2}
                                fill="url(#colorCloseWin)"
                                name={t('close_win')}
                            />
                        </AreaChart>
                    </ResponsiveContainer>

                    {/* Status Summary - 3 trạng thái chính */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                <Typogaphy className="text-xl font-bold text-blue-600">
                                    {transitionsByStatus.contacts.toLocaleString()}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-600">
                                    {t('total_contacts')}
                                </Typogaphy>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3 text-center">
                                <Typogaphy className="text-xl font-bold text-amber-600">
                                    {transitionsByStatus.poc.toLocaleString()}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-600">
                                    {t('poc')}
                                </Typogaphy>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3 text-center">
                                <Typogaphy className="text-xl font-bold text-green-600">
                                    {transitionsByStatus.close_win.toLocaleString()}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-600">
                                    {t('close_win')}
                                </Typogaphy>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default StatusTransitionsChart
